import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Button, Grid } from "antd";
import dayjs from "dayjs";
import React, { useMemo, useState } from "react";
import { weekdayKu } from "../../utils/surgeryAppointments";
import "./appointment-calendar.css";

const MONTHS = ["کانوونی دووەم", "شوبات", "ئازار", "نیسان", "ئایار", "حوزەیران", "تەممووز", "ئاب", "ئەیلوول", "تشرینی یەکەم", "تشرینی دووەم", "کانوونی یەکەم"];
const WEEKDAYS = ["شەممە", "یەکشەممە", "دووشەممە", "سێشەممە", "چوارشەممە", "پێنجشەممە", "هەینی"];
const WEEKDAYS_SHORT = ["ش", "ی", "د", "س", "چ", "پ", "ه"];

function dayKey(date) {
  return date ? dayjs(date).format("YYYY-MM-DD") : "";
}

function clock(time) {
  return time ? String(time).slice(0, 5) : "";
}

function gridStart(month) {
  const first = month.startOf("month");
  return first.subtract((first.day() + 1) % 7, "day");
}

function buildEvents(appointments) {
  const map = {};
  const add = (date, event) => {
    const key = dayKey(date);
    if (!key) return;
    if (!map[key]) map[key] = [];
    map[key].push(event);
  };
  (appointments || []).forEach((item) => {
    const name = item.patient?.fullName || "چارەخواز";
    const patientId = item.patient?.id;
    add(item.surgeryDate, { patientId, name, kind: "surgery", label: "نەشتەرگەری", time: clock(item.surgeryTime) });
    add(item.qalibDate, { patientId, name, kind: "qalib", label: "قالب", time: clock(item.qalibTime) });
    add(item.taqalDate, { patientId, name, kind: "taqal", label: "تەقەڵ", time: clock(item.taqalTime) });
  });
  Object.values(map).forEach((list) => {
    list.sort((a, b) => String(a.time).localeCompare(String(b.time)) || a.name.localeCompare(b.name));
  });
  return map;
}

function AppointmentCalendar({ appointments, query, onOpen }) {
  const screens = Grid.useBreakpoint();
  const [cursor, setCursor] = useState(() => dayjs().startOf("month"));
  const [selected, setSelected] = useState(() => dayjs().format("YYYY-MM-DD"));
  const events = useMemo(() => buildEvents(appointments), [appointments]);
  const q = (query || "").trim();

  const days = useMemo(() => {
    const start = gridStart(cursor);
    return Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
  }, [cursor]);

  const matches = (event) => !q || event.name.includes(q);
  const selectedEvents = (events[selected] || []).filter(matches);
  const today = dayjs().format("YYYY-MM-DD");

  return (
    <section className="apt-cal">
      <div className="apt-cal-head">
        <div>
          <h2>{MONTHS[cursor.month()]} {cursor.year()}</h2>
          <p>نەشتەرگەری، لابردنی قالب و لابردنی تەقەڵ</p>
        </div>
        <div className="apt-cal-nav">
          <Button className="patient-toolbar-btn" icon={<RightOutlined />} onClick={() => setCursor((month) => month.subtract(1, "month"))} />
          <Button
            className="patient-toolbar-btn"
            onClick={() => {
              const now = dayjs();
              setCursor(now.startOf("month"));
              setSelected(now.format("YYYY-MM-DD"));
            }}
          >
            ئەمڕۆ
          </Button>
          <Button className="patient-toolbar-btn" icon={<LeftOutlined />} onClick={() => setCursor((month) => month.add(1, "month"))} />
        </div>
      </div>

      <div className="apt-cal-legend">
        <span className="is-surgery">نەشتەرگەری</span>
        <span className="is-qalib">قالب</span>
        <span className="is-taqal">تەقەڵ</span>
      </div>

      <div className="apt-cal-week">
        {(screens.md ? WEEKDAYS : WEEKDAYS_SHORT).map((name) => (
          <span key={name}>{name}</span>
        ))}
      </div>

      <div className="apt-cal-grid">
        {days.map((day) => {
          const key = day.format("YYYY-MM-DD");
          const items = (events[key] || []).filter(matches);
          const shown = items.slice(0, screens.md ? 4 : 2);
          return (
            <button
              key={key}
              type="button"
              className={`apt-cal-day${day.month() === cursor.month() ? "" : " is-out"}${key === today ? " is-today" : ""}${key === selected ? " is-on" : ""}`}
              onClick={() => setSelected(key)}
            >
              <strong>{day.date()}</strong>
              {shown.map((event, index) => (
                <span key={`${event.kind}-${event.patientId}-${index}`} className={`apt-cal-chip is-${event.kind}`}>
                  {event.time ? `${event.time} ` : ""}
                  {event.name}
                </span>
              ))}
              {items.length > shown.length ? <em>+{items.length - shown.length}</em> : null}
            </button>
          );
        })}
      </div>

      <div className="apt-cal-detail">
        <h3>
          {weekdayKu(selected)} · {dayjs(selected).format("YYYY/MM/DD")}
        </h3>
        {selectedEvents.length === 0 ? (
          <p>هیچ چاوپێکەوتنێک نییە</p>
        ) : (
          <div className="apt-cal-detail-list">
            {selectedEvents.map((event, index) => (
              <button
                key={`${event.kind}-${event.patientId}-${index}`}
                type="button"
                className={`apt-cal-row is-${event.kind}`}
                onClick={() => event.patientId && onOpen?.(event.patientId)}
              >
                <b>{event.time || "—"}</b>
                <span>
                  <strong>{event.name}</strong>
                  <small>{event.label}</small>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default AppointmentCalendar;
